import React, {useState, useEffect} from "react";
import {Skills} from "../types";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Slider,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import 'dayjs/locale/it';
import {SkillAPI} from "../services/API/SkillAPI";
import {SkillCategoryDTO} from "../types/DTOs/SkillCategoryDTO";
import {useTranslation} from "react-i18next";
import {Translate as TranslateIcon} from "@mui/icons-material";
import {SkillTranDTO} from "../types/DTOs/Translation/SkillTranDTO";
import {Translate} from "../utils/Translate";
import {TranslationAPI} from "../services/API/TranslationAPI";
import {SectionTypes} from "../utils/enums";

// ─────────────────────────────────────────────────────────────────────────────
const DEFAULT_LANG = 'original';

// ─────────────────────────────────────────────────────────────────────────────
// PROPS
// ─────────────────────────────────────────────────────────────────────────────
interface EditSkillProps {
    item: Skills | null;
    onSave: () => void;
    onCancel: () => void;
    open: boolean;
    onClose: () => void;
    /** Lingue già tradotte per questo elemento */
    translatedLanguages?: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────
const fetchAvailableLanguages = async (): Promise<{ code: string; i18nKey: string }[]> => {
    return Translate.LanguageForCombo();
};

const fetchSkillTranslation = async (
    skillId: string,
    languageCode: string
): Promise<SkillTranDTO | null | undefined> => {
    const dataTran = await TranslationAPI.Instance<TranslationAPI>().getTranslatedItem(skillId, SectionTypes.SKILL, languageCode);
    if (dataTran === null || dataTran === undefined) return null;
    return dataTran as SkillTranDTO;
};

const saveSkillTranslation = async (data: SkillTranDTO): Promise<void> => {
    await TranslationAPI.Instance<TranslationAPI>().saveTranslateItem(data, SectionTypes.SKILL);
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────────────────────────────────────
export const EditSkill: React.FC<EditSkillProps> = ({
                                                        item,
                                                        onSave,
                                                        onCancel,
                                                        open,
                                                        onClose,
                                                        translatedLanguages = [],
                                                    }) => {
    // ── Stato originale ──
    const [skill, setSkill] = useState<Skills>({
        id: '', userId: '', skillName: '', level: 0, category: '',
        skillsCategoryId: null, categoryName: null,
        createdAt: '', updatedAt: ''
    });

    const [categories, setCategories] = useState<SkillCategoryDTO[]>([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [translatedCategories, setTranslatedCategories] = useState<SkillCategoryDTO[]>([]);

    // ── Stato traduzione ──
    const [availableLanguages, setAvailableLanguages] = useState<{ code: string; i18nKey: string }[]>([]);
    const [selectedLang, setSelectedLang] = useState<string>(DEFAULT_LANG);
    const [translation, setTranslation] = useState<SkillTranDTO>({
        id: '',
        skillId: '',
        category: '',
    });
    const [translationLoading, setTranslationLoading] = useState(false);
    const [translationError, setTranslationError] = useState<string>('');
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const isTranslationMode = selectedLang !== DEFAULT_LANG;

    const { t } = useTranslation();

    // ─────────────────────────────────────────────────────────────────────────
    // EFFECTS
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            setSelectedLang(DEFAULT_LANG);
            setTranslationError('');
            setSaveSuccess(false);

            if (item === null) {
                setSkillDefault();
            } else {
                setSkill(item);
            }

            setCategoriesLoading(true);
            SkillAPI.Instance<SkillAPI>().getSkillCategories()
                .then(setCategories)
                .catch(() => {})
                .finally(() => setCategoriesLoading(false));

            fetchAvailableLanguages()
                .then(setAvailableLanguages)
                .catch(() => {});
        }
    }, [item, open]);

    useEffect(() => {
        if (!isTranslationMode || !skill.id) return;

        setTranslationLoading(true);
        setTranslationError('');
        setSaveSuccess(false);

        fetchSkillTranslation(skill.id, selectedLang)
            .then(data => {
                if (data) {
                    setTranslation(data);
                } else {
                    setTranslation({ id: '', skillId: skill.id, category: '' });
                }
            })
            .catch(() => setTranslationError(t('translation.errorGeneric')))
            .finally(() => setTranslationLoading(false));

        SkillAPI.Instance<SkillAPI>().getSkillCategories(selectedLang)
            .then(setTranslatedCategories)
            .catch(() => {});
    }, [selectedLang]);

    // ─────────────────────────────────────────────────────────────────────────
    // HANDLERS
    // ─────────────────────────────────────────────────────────────────────────
    const setSkillDefault = () => {
        setSkill({
            id: '', userId: '', skillName: '', level: 0, category: '',
            skillsCategoryId: null, categoryName: null,
            createdAt: '', updatedAt: ''
        });
    };

    const handleInputChange = (event: any) => {
        const { name, value } = event.target;
        setSkill(prev => ({ ...prev, [name]: value }));
    };

    const handleTranslationInputChange = (event: any) => {
        const { name, value } = event.target;
        setTranslation(prev => ({ ...prev, [name]: value }));
    };

    const handleLangChange = (e: SelectChangeEvent) => {
        setSelectedLang(e.target.value);
        setSaveSuccess(false);
    };

    const sliderChange = (event: Event, newValue: number, activeThumb: number) => {
        setSkill(prev => ({ ...prev, level: newValue }));
    };

    // Salva originale
    const handleSaveItem = async () => {
        try {
            const payload: any = {
                skillName: skill.skillName,
                level: skill.level,
            };
            if (skill.skillsCategoryId) {
                payload.skillsCategoryId = skill.skillsCategoryId;
            } else if (skill.categoryName) {
                payload.categoryName = skill.categoryName;
            } else {
                payload.category = skill.category || '';
            }

            if (!skill.id) {
                await SkillAPI.Instance<SkillAPI>().createSkills(payload);
            } else {
                await SkillAPI.Instance<SkillAPI>().updateSkills(skill.id, payload);
            }
            setSkillDefault();
            onSave();
        } catch (error) {
            console.error('Error saving skill:', error);
        }
    };

    // Salva traduzione (non chiude la dialog)
    const handleSaveTranslation = async () => {
        setSaveLoading(true);
        setSaveSuccess(false);
        setTranslationError('');
        try {
            await saveSkillTranslation({ ...translation, skillId: skill.id });
            setSaveSuccess(true);
        } catch {
            setTranslationError(t('translation.errorGeneric'));
        } finally {
            setSaveLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // MARKS SLIDER
    // ─────────────────────────────────────────────────────────────────────────
    const marksSlider = [
        { value: 0,   label: t('skillDialog.scaleBeginner') },
        { value: 25,  label: t('skillDialog.scaleBase') },
        { value: 50,  label: t('skillDialog.scaleIntermediate') },
        { value: 75,  label: t('skillDialog.scaleAdvanced') },
        { value: 100, label: t('skillDialog.scaleExpert') },
    ];

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    const langLabel = availableLanguages.find(l => l.code === selectedLang)?.i18nKey ?? '';

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>

            {/* ── Titolo ── */}
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{t('skillDialog.title')}</span>
                    {isTranslationMode && (
                        <Chip
                            icon={<TranslateIcon />}
                            label={t(langLabel)}
                            color="primary"
                            size="small"
                            variant="outlined"
                        />
                    )}
                </Box>
            </DialogTitle>

            {/* ── Banda lingua ── */}
            {skill.id && (
                <Box sx={{ px: 3, py: 1.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>{t('translation.selectLanguageLabel')}</InputLabel>
                        <Select
                            value={selectedLang}
                            label={t('translation.selectLanguageLabel')}
                            onChange={handleLangChange}
                            startAdornment={<TranslateIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />}
                        >
                            <MenuItem value={DEFAULT_LANG}>
                                <em>{t('translation.originalLanguage')}</em>
                            </MenuItem>
                            <Divider />
                            {availableLanguages.map(lang => (
                                <MenuItem key={lang.code} value={lang.code}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {t(lang.i18nKey)}
                                        {translatedLanguages.includes(lang.code) && (
                                            <Chip
                                                label={t('translation.alreadyTranslatedChip')}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                                sx={{ fontSize: '0.65rem', height: 18 }}
                                            />
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isTranslationMode && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {translatedLanguages.includes(selectedLang)
                                ? t('translation.editModeExisting')
                                : t('translation.editModeNew')}
                        </Typography>
                    )}
                </Box>
            )}

            <DialogContent sx={{ pt: 2 }}>

                {translationLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {translationError && (
                    <Alert severity="error" sx={{ mb: 2 }}>{translationError}</Alert>
                )}

                {saveSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>{t('translation.saveSuccess')}</Alert>
                )}

                {!translationLoading && (
                    <>
                        {/* ── Categoria — traducibile ── */}
                        {isTranslationMode ? (
                            <Autocomplete
                                freeSolo
                                options={translatedCategories.map(c => c.name)}
                                value={translation.category || ''}
                                onInputChange={(_event, newValue) => {
                                    setTranslation(prev => ({ ...prev, category: newValue }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        margin="dense"
                                        label={t('skillDialog.category')}
                                        variant="outlined"
                                        sx={{ mb: 2 }}
                                    />
                                )}
                            />
                        ) : (
                            <Autocomplete
                                freeSolo
                                options={categories.map(c => c.name)}
                                value={skill.categoryName || skill.category || ''}
                                onInputChange={(_event, newValue) => {
                                    setSkill(prev => {
                                        const matched = categories.find(
                                            c => c.name.toLowerCase() === newValue.toLowerCase()
                                        );
                                        return {
                                            ...prev,
                                            category: newValue,
                                            skillsCategoryId: matched ? matched.id : null,
                                            categoryName: matched ? null : newValue || null,
                                        };
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        margin="dense"
                                        label={t('skillDialog.category')}
                                        variant="outlined"
                                        sx={{ mb: 2 }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {categoriesLoading ? <CircularProgress size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        )}

                        {/* ── Nome competenza — NON traducibile ── */}
                        <Tooltip
                            title={isTranslationMode ? t('translation.fieldNotTranslatable') : ''}
                            placement="top-start"
                        >
                            <TextField
                                autoFocus
                                name="skillName"
                                margin="dense"
                                label={t('skillDialog.name')}
                                fullWidth
                                variant="outlined"
                                value={skill.skillName}
                                onChange={handleInputChange}
                                disabled={isTranslationMode}
                                sx={{ mb: 2 }}
                            />
                        </Tooltip>

                        {/* ── Livello — NON traducibile ── */}
                        <Tooltip
                            title={isTranslationMode ? t('translation.fieldNotTranslatable') : ''}
                            placement="top-start"
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, opacity: isTranslationMode ? 0.4 : 1 }}>
                                <Slider
                                    aria-label="Livello"
                                    defaultValue={skill.level}
                                    getAriaValueText={() => ''}
                                    step={null}
                                    marks={marksSlider}
                                    onChange={sliderChange}
                                    disabled={isTranslationMode}
                                    sx={{ width: '90%', alignItems: 'center' }}
                                />
                            </Box>
                        </Tooltip>
                    </>
                )}

            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    {t('standardUse.undoBtn')}
                </Button>

                {isTranslationMode ? (
                    <Button
                        onClick={handleSaveTranslation}
                        variant="contained"
                        color="primary"
                        disabled={saveLoading || translationLoading}
                        startIcon={saveLoading
                            ? <CircularProgress size={16} color="inherit" />
                            : <TranslateIcon />
                        }
                    >
                        {saveLoading
                            ? t('translation.savingButton')
                            : t('translation.saveTranslationButton', { lang: t(langLabel) })}
                    </Button>
                ) : (
                    <Button onClick={handleSaveItem} variant="contained">
                        {t('standardUse.saveBtn')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};